import { Component, inject, OnInit } from '@angular/core';
import { EventOriginEnum, NavigateToEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { RelatedEntity } from '@app/core/model/entities/entity';
import { Project, RelatedProject } from '@app/core/model/entities/project/project';
import { ProjectsService } from '@app/features/main/views/projects/projects.service';
import {
  EntityChipsFieldBuilderComponent
} from '@app/shared/components/fields/chips-field-builder/entity-chips-field-builder/entity-chips-field-builder';
import { eq, textContains } from '@app/shared/extra/filter-model-builder';
import { getLocale } from '@app/shared/extra/utils';
import { Filter, SelectParams } from '@app/shared/grid/datasource-utils';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'projects-chips-field-builder',
  templateUrl: './entity-chips-field-builder.html',
  styleUrls: ['../chips-field-builder.component.scss'],
  standalone: false
})
export class ProjectsChipsFieldBuilderComponent extends EntityChipsFieldBuilderComponent<RelatedProject> implements OnInit {
  private projectsService = inject(ProjectsService);

  protected readonly entityType = EntityTypeEnum.PROJECT;
  protected noRelatedEntitiesMessage = 'LABEL.NO_RELATED_PROJECTS';

  /**
   * Create the form, add the chips and hook to the asset value change to update the chip list.
   */
  public ngOnInit(): void {
    super.ngOnInit();

    this.filteredOptions$ = combineLatest([this.loadedEntities, this.selectedEntities]).pipe(
      map(([entities, chips]) => {
          let filteredEntities = entities.filter(e => !chips.map(c => c.id).includes(e.id));
          if (this.entity instanceof Project) {
            filteredEntities = filteredEntities.filter(project => this.entity.id !== project.id);
          }
          return filteredEntities;
        }
      )
    );
  }

  /**
   * Load projects next page.
   * @param startingIndex Start of the page to load.
   * @return Observable<RelatedEntity[]> A list of RelatedEntity matching the form filters.
   */
  protected loadEntitiesNextPage(startingIndex: number): Observable<RelatedEntity[]> {
    // Do not fetch next page if the module is not allowed or if there is no current asset
    if (!this.assetTypeAllowsEntityModule || !this.assetChanged$.value) return of([]);

    // Build filters
    const filters: Filter[] = [];

    if (this.assetChanged$.value) {
      filters.push(eq('assetIds', this.assetChanged$.value));
    }

    if (this.form.controls.field.value) {
      filters.push(textContains('computedProperties.displayName', this.form.controls.field.value));
    }

    // Build Select Params
    const selectParams: SelectParams = {
      locale: getLocale(),
      sorts: [
        {
          field: 'lastChangeDate',
          direction: 'DESC'
        }
      ],
      filters,
      start: startingIndex,
      count: this.appConfig.PREFERRED_REQUEST_PAGE_SIZE
    };

    // Load projects page
    return this.projectsService.loadProjectsPage(selectParams)
      .pipe(map(projectsPage => projectsPage.map(project => project.toRelatedProject())),);
  }

  /**
   * Navigate to Project sheet
   * @param relatedEntityId Project ID
   */
  public async navigateToRelatedEntitySheet(relatedEntityId: string): Promise<void> {
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.FIELD_CLICK,
      NavigateToEnum.SHEET,
      EntityTypeEnum.PROJECT,
      relatedEntityId
    );
    await this.projectsService.navigateToProjectSheet(relatedEntityId);
  }
}
